import { cva } from "class-variance-authority";

export { default as Dialog } from "./Dialog.vue";
export { default as DialogContent } from "./DialogContent.vue";
export { default as DialogClose } from "./DialogClose.vue";
export { default as DialogFooter } from "./DialogFooter.vue";
export { default as DialogHeader } from "./DialogHeader.vue";
export { default as DialogSearch } from "./DialogSearch.vue";
export {
    DialogDescription,
    DialogRoot,
    DialogTitle,
    DialogTrigger,
} from "radix-vue";

export const overlayVariants = cva(
    [
        "inset-0",
        "z-50",
        "bg-black/50",
        "data-[state=closed]:animate-out",
        "data-[state=open]:animate-in",
        "data-[state=closed]:fade-out-0",
        "data-[state=open]:fade-in-0",
    ],
    {
        variants: {
            type: {
                fixed: ["fixed"],
                absolute: ["absolute"],
            },
        },
        compoundVariants: [
            {
                type: "absolute",
                side: "default",
            },
            {
                type: "absolute",
                side: "bottom",
                class: ["-mt-px"],
            },
            {
                type: "absolute",
                side: "default",
                class: ["rounded-xl"],
            },
            {
                type: "absolute",
                side: "bottom",
                class: ["rounded-t-2xl"],
            },
            {
                type: "absolute",
                variant: ["create"],
                class: ["bg-transparent"],
            },
        ],
        defaultVariants: {
            type: "fixed",
            variant: "default",
            side: "default",
        },
    }
);

export const contentVariants = cva(
    [
        "isolate",
        "flex",
        "flex-col",
        "z-50",
        "shadow-lg",
        "transition",
        "ease-in-out",
        "data-[state=closed]:animate-out",
        "data-[state=open]:animate-in",
        "data-[state=closed]:duration-300",
        "data-[state=open]:duration-300",
        "focus-visible:outline-none",
        "[&_#footer]:z-10",
    ],
    {
        variants: {
            type: {
                fixed: ["fixed"],
                absolute: ["absolute"],
            },
            variant: {
                default: [],
                modal: ["w-full", "duration-200"],
                create: [
                    "w-full",
                    "md:max-w-[500px]",
                    "md:max-h-[min(100dvh-48px,660px)]",
                ],
                "task-detail": [
                    "md:max-w-[1012px]",
                    "md:has-data-collapsed:max-w-[548px]",
                    "md:transition-max-width",
                ],
                passport: [
                    "max-w-full",
                    "lg:max-w-[500px]",
                    "md:transition-max-width",
                    "[&_#footer]:bg-primary",
                ],
                property: [
                    "w-full",
                    "h-full",
                    "md:h-2/3",
                    "md:max-w-[400px]",
                    "lg:max-w-[480px]",
                    "md:max-h-160",
                ],
                log: [
                    "w-full",
                    "md:max-w-[400px]",
                    "lg:max-w-[480px]",
                    "[&_#footer]:pt-4",
                    "[&_#footer]:px-4",
                    "md:[&_#footer]:pt-8",
                    "md:[&_#footer]:px-6",
                    "md:[&_#footer]:pb-6",
                    "md:[&_#footer]:border-t-0",
                ],
            },
            background: {
                white: ["bg-primary"],
                gray: ["bg-tertiary"],
            },
            size: {
                full: ["inset-0"],
                600: ["h-[600px]"],
                700: ["h-[700px]"],
                carousel: [
                    "w-full",
                    "h-full",
                    "lg:w-auto",
                    "lg:h-auto",
                    "md:max-w-[calc(100dvw-64px)]",
                    "md:max-h-[calc(100dvh-64px)]",
                    "lg:max-w-[calc(60dvw-64px)]",
                ],
                "bulk-actions": [
                    "w-[600px]",
                    "min-h-[min(100dvh-48px,400px)]",
                    "max-h-[min(100dvh-48px,600px)]",
                ],
            },
            side: {
                default: [
                    "data-[state=closed]:fade-out-0",
                    "data-[state=open]:fade-in-0",
                ],
                top: [
                    "inset-x-0",
                    "top-0",
                    "border-b",
                    "data-[state=closed]:slide-out-to-top",
                    "data-[state=open]:slide-in-from-top",
                ],
                bottom: [
                    "inset-x-0",
                    "bottom-0",
                    "border-t",
                    "data-[state=closed]:slide-out-to-bottom",
                    "data-[state=open]:slide-in-from-bottom",
                    "max-h-[calc(100dvh-32px)]",
                ],
                left: [
                    "w-3/4",
                    "h-[calc(100%-24px)]",
                    "inset-y-3",
                    "left-3",
                    "data-[state=closed]:slide-out-to-left",
                    "data-[state=open]:slide-in-from-left",
                ],
                right: [
                    "w-3/4",
                    "h-[calc(100%-24px)]",
                    "inset-y-3",
                    "right-3",
                    "data-[state=closed]:slide-out-to-right",
                    "data-[state=open]:slide-in-from-right",
                ],
            },
        },
        compoundVariants: [
            {
                variant: ["default", "modal"],
                side: ["left", "right"],
                class: ["md:max-w-md"],
            },
            {
                variant: ["modal"],
                side: "left",
                class: ["inset-y-0", "left-0", "h-full"],
            },
            {
                variant: ["modal"],
                side: "right",
                class: ["inset-y-0", "right-0", "h-full"],
            },
            {
                variant: ["default", "nav"],
                side: "bottom",
                class: ["rounded-t-xl", "[&>div:first-of-type]:rounded-t-xl"],
            },
            {
                variant: ["log"],
                side: "bottom",
                class: ["rounded-t-md", "[&>div:first-of-type]:rounded-t-md"],
            },
            {
                variant: ["task-detail", "passport"],
                side: "default",
                class: [
                    "w-full",
                    "md:w-[calc(100%-24px)]",
                    "md:max-h-[calc(100dvh-24px)]",
                    "md:inset-3",
                ],
            },
            {
                variant: [
                    "default",
                    "modal",
                    "create",
                    "task-detail",
                    "passport",
                    "property",
                    "log",
                ],
                side: "default",
                class: [
                    "md:rounded-xl",
                    "md:[&>div:first-of-type]:rounded-t-xl",
                    "md:[&>div:last-of-type]:rounded-b-xl",
                ],
            },
            {
                variant: ["default", "modal", "create", "property", "log"],
                side: ["top", "bottom"],
                class: ["h-fit"],
            },
            {
                variant: ["default", "modal", "task-detail", "passport"],
                side: ["left", "right"],
                class: [
                    "rounded-xl",
                    "[&>div:first-of-type]:rounded-t-xl",
                    "[&>div:last-of-type]:rounded-b-xl",
                ],
            },
            {
                variant: ["default", "task-detail", "passport", "log"],
                side: "default",
                size: "full",
                nested: false,
                class: ["max-h-[100dvh]"],
            },
            {
                variant: ["default", "task-detail", "passport"],
                side: "default",
                size: "full",
                nested: true,
                class: ["inset-4"],
            },
            {
                variant: ["modal"],
                side: "default",
                nested: false,
                class: ["max-w-[400px]"],
            },
            {
                variant: "modal",
                side: "top",
                class: [
                    "mb-24",
                    "rounded-b-2xl",
                    "[&>div:last-of-type]:rounded-b-2xl",
                ],
            },
            {
                variant: ["modal", "create", "property"],
                side: "bottom",
                class: [
                    "mt-24",
                    "rounded-t-2xl",
                    "[&>div:first-of-type]:rounded-t-2xl",
                    "pb-0",
                ],
            },
            {
                variant: "modal",
                side: ["left", "right"],
                class: ["h-full", "[&_#footer]:mt-auto"],
            },
            {
                size: ["default", 600, 700, "carousel", "fit", "bulk-actions"],
                side: "default",
                class: [
                    "!left-1/2",
                    "!top-1/2",
                    "!-translate-x-1/2",
                    "!-translate-y-1/2",
                    "data-[state=closed]:zoom-out-95",
                    "data-[state=open]:zoom-in-95",
                    "data-[state=closed]:slide-out-to-top-[48%]",
                    "data-[state=open]:slide-in-from-top-[48%]",
                    "data-[state=closed]:slide-out-to-left-1/2",
                    "data-[state=open]:slide-in-from-left-1/2",
                ],
            },
            {
                variant: "create",
                nested: true,
                class: [
                    "md:min-w-full",
                    "md:min-h-full",
                    "md:h-fit",
                    "md:max-h-[min(100dvh-48px,660px)]",
                    "md:top-1/2",
                    "md:-translate-y-1/2",
                    "md:data-[state=closed]:slide-out-to-top-[50%]",
                    "md:data-[state=open]:slide-in-from-top-[50%]",
                ],
            },
            {
                size: "full",
                side: "bottom",
                class: ["h-fit", "top-auto"],
            },
            {
                variant: ["property"],
                size: "fit",
                class: ["md:h-fit"],
            },
        ],
        defaultVariants: {
            type: "fixed",
            variant: "default",
            background: "gray",
            size: "default",
            side: "default",
            nested: false,
        },
    }
);

export const headerVariants = cva([], {
    variants: {
        variant: {
            default: [
                "flex",
                "flex-col",
                "pt-6",
                "px-6",
                "[&_.close]:absolute",
            ],
        },
        theme: {
            default: [
                "[&_#icon-wrapper]:bg-tertiary",
                "[&_#icon]:featured-icon-light-fg-gray",
            ],
            success: [
                "[&_#icon-wrapper]:bg-success-secondary",
                "[&_#icon]:featured-icon-light-fg-success",
            ],
            warning: [
                "[&_#icon-wrapper]:bg-warning-secondary",
                "[&_#icon]:featured-icon-light-fg-warning",
            ],
            info: [
                "[&_#icon-wrapper]:bg-brand-secondary",
                "[&_#icon]:featured-icon-light-fg-brand",
            ],
            error: [
                "[&_#icon-wrapper]:bg-error-secondary",
                "[&_#icon]:featured-icon-light-fg-error",
            ],
        },
        icon: {
            true: ["gap-3", "md:gap-4"],
        },
    },
    compoundVariants: [
        {
            side: "default",
            class: ["[&_.close]:top-4", "[&_.close]:right-4"],
        },
        {
            side: ["top", "bottom"],
            class: ["[&_.close]:top-2", "[&_.close]:right-2"],
        },
        {
            variant: "default",
            side: ["left", "right"],
            class: [
                "p-4",
                "pb-2",
                "pl-6",
                "[&_#dialog-title]:text-brand-primary",
                "[&_#dialog-title]:font-medium",
                "[&_#dialog-title]:text-style-2xl",
                "[&_.close]:hidden",
            ],
        },
    ],
    defaultVariants: {
        variant: "default",
        theme: "default",
        side: "default",
        icon: false,
    },
});
